/**
 * Web implementation of the platform-neutral {@link AnalyticsReporter} port (GA4).
 *
 * Wraps the `firebase/analytics` JS SDK instance that `FirebaseContainer` already initializes on web
 * (`fbAnalytics`). Injected once at startup via `setAnalyticsReporter()` in `startApp` — mirroring how
 * `lib/sentry.ts` injects the `ErrorReporter`. Kept out of the shared `client-core` layer because the
 * `firebase/analytics` module is web-only (it crashes the React Native Metro bundler); the RN app
 * injects its own adapter over `@react-native-firebase/analytics`.
 */
import type { Analytics } from 'firebase/analytics';

import type { AnalyticsReporter } from '@elara/client-core/lib/analytics';

import { logEvent, setDefaultEventParameters, setUserId, setUserProperties } from 'firebase/analytics';

/** Options for {@link createWebAnalyticsReporter}. */
export interface WebAnalyticsReporterOptions {
  /**
   * When true, tag every event with `traffic_type=internal` regardless of who is signed in — used in
   * development and Firebase Hosting preview channels so that non-production traffic can be excluded
   * from GA4 reports via an Internal Traffic data filter.
   */
  forceInternal?: boolean;
}

/**
 * Builds the GA4-backed reporter over an initialized web {@link Analytics} instance. Tags the session
 * with `platform: 'web'` up front so reports can split web from mobile traffic.
 */
export function createWebAnalyticsReporter(
  analytics: Analytics,
  options: WebAnalyticsReporterOptions = {},
): AnalyticsReporter {
  const { forceInternal = false } = options;
  setUserProperties(analytics, { platform: 'web' });
  // In dev/preview, tag internal traffic before auth resolves so early events (the first screen view)
  // are covered too. `setDefaultEventParameters` on web takes only the params bag (no instance).
  if (forceInternal) {
    setDefaultEventParameters({ traffic_type: 'internal' });
  }

  return {
    logEvent(name, params) {
      logEvent(analytics, name, params);
    },
    logScreenView(screenName) {
      // GA4's recommended screen-view shape. `page_path` is intentionally not forwarded: on web GA4
      // already captures the browser URL, so the low-cardinality screen name is what adds value here.
      logEvent(analytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenName,
      });
    },
    setUserId(userId) {
      // The SDK clears the id when passed `null`.
      setUserId(analytics, userId ?? null);
    },
    setUserProperties(props) {
      setUserProperties(analytics, props);
    },
    setInternalTraffic(isInternal) {
      // Applied as a GA4 default event parameter so it rides on every subsequent event, including
      // automatically-collected ones. `null` clears it so a staff user signing out (then a
      // non-staff user signing in) stops being tagged. `forceInternal` keeps dev/preview tagged.
      setDefaultEventParameters({ traffic_type: isInternal || forceInternal ? 'internal' : null });
    },
  };
}

/**
 * Strips the share token out of a shared-itinerary URL (`/share/<token>` → `/share/:token`) so the
 * credential never reaches GA4 as `page_location`. Every other URL is returned unchanged.
 *
 * @example
 * redactAnalyticsUrl('https://app.elara.travel/share/abc123?x=1'); // 'https://app.elara.travel/share/:token'
 */
export function redactAnalyticsUrl(href: string): string {
  try {
    const url = new URL(href);
    if (!url.pathname.startsWith('/share/')) return href;
    url.pathname = '/share/:token';
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return href;
  }
}
